import { union } from '3rd-party-wrappers/turf';

import { overlayLayers } from 'constants/imagery';
import { LatLngBounds } from 'mapping/leaflet';
import { groupBy, mapValues, startCase } from 'lodash';
import { formatDateToCalendarYear, formatDateToUnix } from 'util/formatters/dateFormatter';
import { stripDomain } from 'util/urls';

export const groupSsurgoGeoJsonByParsedMuname = (features) => {

  /* TODO - 5.7.3 union() call below should be changed to calculateUnion(). This reversion was made for a
     P1 hotfix. union() copies .properties of geojson which is currently needed. calculateUnion()
     intentionally does not. To change this to calculateUnion() it's necessary to fix the store structure
     so that soil type is not kept in .properties. The issue with putting data here is that it ends up
     coupling business logic to presentation. And when we perform different operations on geometry, those
     functions end up needing to know how to transfer the business data around correctly. */
  return mapValues(groupBy(features, `properties.parsed-muname`), (featuresForParsedMuname) =>
    featuresForParsedMuname.length === 1 ? featuresForParsedMuname[0] : union(...featuresForParsedMuname)
  );
};

export const parseSsurgoGeoJson = ( featureCollection, metaData ) => {
  // Convert to behave like actual script zones
  const { features } = featureCollection;
  const groupedFeatures = groupSsurgoGeoJsonByParsedMuname(features);
  const zones = {};
  Object.values(groupedFeatures).forEach( (feature, index) => {
    const zoneId = `zone-${index}`;
    zones[zoneId] = {
      geoJson: {
        geometry: feature.geometry,
        type: feature.type,
        properties: feature.properties
      },
      meta: {
        geometry: feature.geometry,
        index,
        zoneId
      }
    };
  });

  return {
    [overlayLayers.SOIL]: {
      [formatDateToUnix(metaData.date)]: zones
    }
  };
};

export const parseSsurgoDataList = ( dump ) => {
  const list = dump.data;
  let metaData = {};
  let isoDate;
  list.forEach( (imageData) => {
    const image = {};
    const fieldId = imageData.field.id;
    image.fieldId = fieldId;
    image.layer = imageData.tag;
    image.date = imageData.reference;
    image.displaySeason = formatDateToCalendarYear(image.date);
    image.processDate = imageData.created;
    image.version = imageData.version;

    imageData.images.forEach( (imageDetails) => {
      image.boundingBox = imageDetails['bounding-box'];
      const cacheUri = imageDetails['cache-uri'] || '';
      image.url = stripDomain(cacheUri);
      image.previewUrl = imageDetails['preview-uri'];
      image.rawUrl = imageDetails['raw-uri'];
      image.id = imageDetails.id;
      image.contentType = imageDetails['content-type'];
    });
    // prune down to max of one
    if(!isoDate || image.date > isoDate) {
      isoDate = image.date;
      metaData = image;
    }
  });

  return metaData;
};

export const parseFieldHealthImagery = ( dump ) => {
  const images = {};
  const list = dump.data;
  list.forEach( (imageData) => {
    const image = {};
    const fieldId = imageData.field.id;
    const details = imageData['layer-data']; // data that is both here and inside imageData.images[0]['layer-data']
    image.fieldId = fieldId;
    image.date = imageData.reference;
    image.displaySeason = formatDateToCalendarYear(image.date);
    image.overlayUuid = image.date; // In some overlay data, the date may not be unique
    image.processDate = imageData.created;
    image.version = imageData.version;

    image.provider = details.product; // I refuse to further overload "product"
    image.quality = details['visual-quality'];
    image.mask = details['mask-percentages'];
    image.cciVariability = details['cci-variability'];
    image.cciBuckets = details['cci-buckets'];
    switch(imageData.tag) {
      case 'abs':
        image.layer = overlayLayers.VEGETATION;
        break;
      case 'rank':
        image.layer = overlayLayers.SCOUTING;
        break;
      case 'raw':
        image.layer = overlayLayers.RAW;
        break;
      case 'true-color':
        image.layer = overlayLayers.TRUE_COLOR;
        break;
      case 'yield':
        image.layer = overlayLayers.MOSAIC_YIELD;
        break;
      default:
        // We were warning here, but mosaic started adding many layers, so, meh.
        break;
    }
    if(!image.layer) {
      return; // Don't risk data shape mismatches with any unknown layers
    }
    const imageDetails = imageData.images[0]; // Because service people like this pattern
    const imageLayerData = imageDetails['layer-data']; // Some repetition with outer layer, some differences
    image.boundingBox = imageDetails['bounding-box'];
    const box = image.boundingBox;
    image.bounds = new LatLngBounds( [ [box['max-y'], box['max-x']], [box['min-y'], box['min-x']] ] );
    image.url = imageDetails['cache-uri'];
    image.previewUrl = imageDetails['preview-uri']; // Note Url, not Uri (consistency with others)
    image.rawUrl = imageDetails['raw-uri'];
    image.size = imageLayerData.size;
    image.id = imageDetails.id;
    image.contentType = imageDetails['content-type'];
    image.product = imageLayerData.product || image.product; // rapideye is in both, LC8 only in inner ¯\_(ツ)_/¯
    image.colorScheme = imageLayerData['veg-color-scheme']; // TODO: Need different legends for different versions?
    images[image.layer] = images[image.layer] || {};
    images[image.layer][image.overlayUuid] = image;
  });

  return images;
};

export const parseV3FieldHealthImagery = (dump) => {
  const images = {};
  const list = dump.data;

  list.forEach((imageData) => {
    const image = {};
    const fieldId = imageData.fieldId;
    const details = imageData.meta;

    image.fieldId = fieldId;
    image.date = imageData.eventDate;
    image.displaySeason = formatDateToCalendarYear(image.date);
    image.overlayUuid = image.date; // In some overlay data, the date may not be unique
    image.processDate = imageData.createdAt;
    image.version = imageData.meta?.['image-version'];

    image.provider = details.product;
    image.quality = null; // `v3` does not have a `visual-quality` field
    image.mask = details['mask-percentages'];
    image.cciVariability = details['cci-variability'];
    image.cciBuckets = null; // `v3` does not have `cci-buckets`

    switch (imageData.layer) {
      case 'fha.abs':
        image.layer = overlayLayers.VEGETATION;
        break;
      case 'fha.rank':
        image.layer = overlayLayers.SCOUTING;
        break;
      case 'fha.raw':
        image.layer = overlayLayers.RAW;
        break;
      case 'fha.true-color':
        image.layer = overlayLayers.TRUE_COLOR;
        break;
      case 'fha.yield':
        image.layer = overlayLayers.MOSAIC_YIELD;
        break;
      default:
        // Ignore unknown layers
        return;
    }

    if (!image.layer) {
      return; // Don't risk data shape mismatches with any unknown layers
    }

    const imageDetails = imageData.payload[1]; // Use the first payload item
    if(!imageDetails) {
      return; // Skip if no image details are available
    }

    const bbox = imageDetails.meta.bbox;

    image.boundingBox = {
      'max-x': bbox.northeast.coordinates[0],
      'min-x': bbox.southwest.coordinates[0],
      'max-y': bbox.northeast.coordinates[1],
      'min-y': bbox.southwest.coordinates[1],
    };

    image.bounds = new LatLngBounds([
      [image.boundingBox['max-y'], image.boundingBox['max-x']],
      [image.boundingBox['min-y'], image.boundingBox['min-x']],
    ]);

    image.url = imageDetails.uri;
    image.previewUrl = imageDetails.uri; // `v3` does not have a separate preview URL
    image.rawUrl = imageDetails.uri; // `v3` does not have a separate raw URL
    image.size = null; // `v3` does not have a `size` field
    image.id = imageDetails.uuid;
    image.contentType = imageDetails.contentType;
    image.product = details.product;
    image.colorScheme = details['veg-color-scheme'];

    images[image.layer] = images[image.layer] || {};
    images[image.layer][image.overlayUuid] = image;
  });

  return images;
};

export const parseSoilSampleImagery = ( dump ) => {
  // data spec available at https://docs.google.com/document/d/1CO4hBUfZnou2u2c9p_6D46OoL0qTLqNaLyE-DFvtjxc/edit#
  const nutrients = {};
  const imagePayloadFor = {};
  const list = dump.data.filter(e => !e.deleted);

  // The image payloads are addressed by metadataTag and may not be in the same order as the measurement data
  // sneakily, they sometimes (most times?) are the same order
  list.forEach( (entry) => {
    entry.payload = entry.payload.filter( content => content.contentType === 'image/png');
    entry.payload.forEach( content => {
      imagePayloadFor[content.meta.metadataTag] = content;
    });
  });

  // An array of dates,
  // each of which has an array of depths,
  // each of which has an array of measurements,
  // each of which are for a different nutrient
  // We flip that into a set of nutrients,
  // each of which has an object of date/depth combinations
  list.forEach( (entry) => {
    entry.meta.depths.forEach( (depth) => {
      depth.measurements.forEach( measurement => {

        const imageData = imagePayloadFor[ measurement.metadataTag ]; // payloads are in separate object tree branch
        const layer = `soil_sample_${measurement.nutrient}`.toLowerCase();
        nutrients[layer] = nutrients[layer] || {};

        const sample = {}; // the final object
        sample.date = entry.eventDate;
        sample.displaySeason = formatDateToCalendarYear(sample.date);
        sample.fieldId = entry.fieldId;
        sample.layer = layer;
        sample.uuid = imageData.uuid;
        sample.overlayUuid = `${sample.date}_${sample.uuid}`;
        sample.contentType = imageData.contentType;
        sample.url = stripDomain(imageData.uri);
        sample.previewUrl = sample.url;
        sample.startDepth = depth.start;
        sample.endDepth = depth.end;
        sample.displayDepth = sample.endDepth ? Math.round(depth.end.q * 39.3700787) : ''; // meters to inches, needs i18n
        sample.bbox = imageData.meta.bbox;
        const bbox = imageData.meta.bbox;
        sample.boundingBox = {
          'max-x': bbox.southwest.coordinates[0],
          'min-x': bbox.northeast.coordinates[0],
          'max-y': bbox.northeast.coordinates[1],
          'min-y': bbox.southwest.coordinates[1]
        };
        const box = sample.boundingBox;
        sample.bounds = new LatLngBounds( [ [box['max-y'], box['max-x']], [box['min-y'], box['min-x']] ] );
        sample.legend = {
          layer: layer,
          labelKey: `label-text__overlay-legend--${layer}`,
          colors: measurement.legend.map( (legend) => {
            return { low: legend.lowColor, high: legend.highColor };
          }).reverse(),
          displayValues: measurement.legend.map( (legend) => `${legend.low} - ${legend.high}` ).reverse()
        };
        nutrients[layer][sample.overlayUuid] = sample;
      });
    });
  });
  return nutrients;
};

export const parseNonSatImagery = ( dump ) => {
  const images = {};
  const list = dump.data;
  list.forEach( (imageData) => {
    const baseImage = {};
    const layer = imageData.layer.replace(/\./g, '-');
    const legend = imageData.meta.legend || [];
    baseImage.fieldId = imageData.fieldId;
    baseImage.date = imageData.eventDate;
    baseImage.displaySeason = formatDateToCalendarYear(baseImage.date);
    baseImage.layer = layer;
    baseImage.name = imageData.meta.name;
    baseImage.vendor = startCase(imageData.meta.vendor);
    baseImage.legend = {
      layer: layer,
      labelKey: `label-text__overlay-legend--${layer}`,
      colors: legend.map( (thisLegend) => {
        if (!thisLegend.color.startsWith('#')) {
          return `#${thisLegend.color}`;
        }
        return thisLegend.color;
      }).reverse(),
      displayValues: legend.map( (thisLegend, index ) => {
        return thisLegend.low === thisLegend.high && index === legend.length - 1
          ? `> ${thisLegend.high}`
          : `${thisLegend.low} - ${thisLegend.high}`;
      }).reverse()
    };
    imageData.payload.forEach( (payloadImage) => {
      const image = {...baseImage};
      image.contentType = payloadImage.contentType;
      const bbox = payloadImage.meta.bbox;
      image.boundingBox = {
        'max-x': bbox.southwest.coordinates[0],
        'min-x': bbox.northeast.coordinates[0],
        'max-y': bbox.northeast.coordinates[1],
        'min-y': bbox.southwest.coordinates[1]
      };
      const box = image.boundingBox;
      image.bounds = new LatLngBounds( [ [box['max-y'], box['max-x']], [box['min-y'], box['min-x']] ] );
      image.url = payloadImage.uri; // TODO - drop domain?
      image.previewUrl = image.url;
      image.uuid = payloadImage.uuid;
      image.overlayUuid = `${image.date}_${image.uuid}`;
      images[overlayLayers.OTHER] = images[overlayLayers.OTHER] || {};
      images[overlayLayers.OTHER][image.overlayUuid] = image;
    });
  });
  return images;
};

export const parseImportedMapsGeoJson = (rawGeoJsons, metaData) => {
  const parsedImportedMaps = rawGeoJsons.reduce((parsed, rawGeoJson, index) => {
    const { features } = rawGeoJson;
    const parsedZones = features.reduce((zones, feature, index) => {
      const zoneId = feature.properties['zone-id'];
      zones[zoneId] = {
        geoJson: {
          geometry: feature.geometry,
          type: feature.type,
          properties: feature.properties
        },
        meta: {
          geometry: feature.geometry,
          index,
          zoneId
        }
      };

      return zones;
    }, {});

    const meta = metaData[index];
    parsed[formatDateToUnix(meta.date)] = { meta, zones: parsedZones };
    return parsed;
  }, {});

  return {
    [overlayLayers.IMPORTED_MAPS]: parsedImportedMaps
  };
};

export const parseImportedMapsList = ({ data }) => {
  return data
  .filter(({ deleted }) => !deleted)
  .map(({ fieldId, eventDate, meta, payload }) => {
    return {
      date: eventDate,
      fieldId,
      id: meta.id,
      name: meta.name,
      url: payload[0].uri,
      polygonal: meta.polygonal
    };
  });
};

export const parseSsurgoV4 = ({ data }) => {
  const [newestSsurgoLayer] = data
  .filter(({ deleted }) => !deleted)
  .toSorted((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

  if (!newestSsurgoLayer) return {};

  return {
    date: newestSsurgoLayer.eventDate,
    url: stripDomain(newestSsurgoLayer.payload[0].uri)
  };
};
